import { useMemo, useRef, useState, type CSSProperties } from 'react';

import { handlebarsTokenRe, parseHandlebarsTokens } from '../../lib/handlebars-tokens';

/**
 * Agent-instructions textarea that's aware of Handlebars `{{token}}`
 * references. Three things it does that a plain `<textarea>` doesn't:
 *
 *  1. Highlights every `{{token}}` inline (accent blue) using a styled
 *     backdrop div that exactly mirrors the textarea's layout. The
 *     textarea sits transparent on top so the caret + selection still
 *     work natively; the colored render comes from the backdrop. Scroll
 *     position is synced on input/scroll so the highlight tracks the
 *     visible content.
 *  2. Renders each unique token as a clickable chip below the field.
 *     Click a chip to focus the textarea and select the token's first
 *     occurrence — fast navigation for long system prompts.
 *  3. Surfaces every variable the prompt assumes, at a glance. Authors
 *     spot typos and dead refs without scanning a 200-line template.
 *
 * Coverage analysis (is this token actually provided at runtime?) is
 * intentionally NOT done here: an Agent is reusable across many
 * `llm-agent` nodes with different `inputMapping`s, so the agent page
 * has no single "input" to validate against. That check lives in
 * `LlmAgentConfig` (workflow node inspector), where the per-node input
 * mapping and the workflow's trigger inputSchema are in scope. Keep
 * this component honest — chips here are informational, not a contract.
 *
 * Drop-in replacement for `<textarea>` — same `value` / `onChange`
 * contract, same `className="textarea"` for theming consistency.
 */
export interface IHandlebarsInstructionsEditorProps {
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  rows?: number;
  placeholder?: string;
  /**
   * When `false`, omits the `References:` chip row rendered below the
   * textarea. Useful when a parent surface (e.g. the workflow-node
   * goal field) renders its own coverage / available-input chips and
   * the built-in row would just duplicate them.
   *
   * Defaults to `true` to keep existing call sites (agent editor, new
   * agent page) unchanged.
   */
  showReferences?: boolean;
}

interface IFoundToken {
  name: string;
  start: number;
  end: number;
}

// Positional parse (start/end offsets) for the highlight backdrop. Unique
// names come from the shared `parseHandlebarsTokens`; this keeps offsets the
// backdrop needs. Both walk the same `handlebarsTokenRe`.
function parseTokens(text: string): IFoundToken[] {
  const out: IFoundToken[] = [];
  for (const m of text.matchAll(handlebarsTokenRe)) {
    if (m.index === undefined) continue;
    const name = m[1];
    if (!name) continue;
    out.push({ name, start: m.index, end: m.index + m[0].length });
  }
  return out;
}

// Highlighted `{{token}}` style. Token text uses the surface's
// default foreground so contrast stays maximal on light + dark
// themes; the accent carries through the background tint, which
// was previously a faint 0.18 alpha — bumped to 0.32 so the chip
// reads as confidently highlighted (still readable, not loud).
const tokenColor = 'var(--text-primary)';
const tokenBg = 'var(--surface-accent, rgba(29, 78, 216, 0.32))';
const tokenBorder = 'var(--status-accent, #1d4ed8)';

const chipBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontFamily: 'var(--font-mono, monospace)',
  fontSize: 11,
  padding: '2px 6px',
  borderRadius: 4,
  border: `1px solid ${tokenBorder}`,
  background: tokenBg,
  color: tokenColor,
  fontWeight: 500,
  cursor: 'pointer',
  userSelect: 'none',
};

/**
 * Style block applied to BOTH the textarea AND the backdrop div so the
 * caret in the textarea lines up pixel-for-pixel with the colored tokens
 * in the backdrop. Any change to one must mirror to the other.
 */
const sharedTextStyle: CSSProperties = {
  fontFamily: 'inherit',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  letterSpacing: 'inherit',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  padding: 0,
  margin: 0,
  border: 0,
};

export function HandlebarsInstructionsEditor({
  value,
  onChange,
  required,
  rows = 10,
  placeholder,
  showReferences = true,
}: IHandlebarsInstructionsEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  const [focusedToken, setFocusedToken] = useState<string | null>(null);

  const tokens = useMemo(() => parseTokens(value), [value]);
  const tokenNames = useMemo(() => parseHandlebarsTokens(value), [value]);

  // Build a sequence of plain-text and token spans for the backdrop. We
  // include a trailing space inside the last plain slice so a final
  // newline still has visible vertical room (otherwise the backdrop
  // collapses one line short of the textarea on the last keystroke).
  const segments = useMemo<Array<{ type: 'text' | 'token'; text: string }>>(() => {
    const out: Array<{ type: 'text' | 'token'; text: string }> = [];
    let cursor = 0;
    for (const tok of tokens) {
      if (tok.start > cursor) {
        out.push({ type: 'text', text: value.slice(cursor, tok.start) });
      }
      out.push({ type: 'token', text: value.slice(tok.start, tok.end) });
      cursor = tok.end;
    }
    if (cursor < value.length) {
      out.push({ type: 'text', text: value.slice(cursor) });
    }
    return out;
  }, [value, tokens]);

  const syncScroll = () => {
    const ta = textareaRef.current;
    const bd = backdropRef.current;
    if (!ta || !bd) return;
    bd.scrollTop = ta.scrollTop;
    bd.scrollLeft = ta.scrollLeft;
  };

  const jumpTo = (name: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const first = tokens.find((t) => t.name === name);
    if (!first) return;
    el.focus();
    el.setSelectionRange(first.start, first.end);
    setFocusedToken(name);
    requestAnimationFrame(syncScroll);
  };

  // Constraining the wrapper to the textarea's intrinsic height (driven
  // by `rows`) keeps the backdrop the same size. Hardcoding a px height
  // would diverge from the user's theme line-height.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <div
        style={{
          position: 'relative',
          // Pronounced wrapper border. We move the visible border off the
          // `.textarea` (transparent + same-position layer underneath) onto
          // this outer wrapper so the user gets one obvious frame around
          // the highlighted instructions surface rather than two stacked
          // borders bleeding through each other.
          border: '2px solid var(--border-strong, var(--text-secondary))',
          borderRadius: 'var(--radius-md, 6px)',
          // One shade darker than the prior `--surface-1` so the editor
          // visually distinguishes from the surrounding card. Kept light
          // enough that the body text (which renders in --text-primary
          // on the backdrop) stays readable — see the previous regression
          // where over-darkening made text disappear.
          background: 'var(--surface-2, var(--surface-1))',
        }}
      >
        {/* Highlight backdrop — sits behind the transparent textarea.
            Same padding / font as the textarea (inherited via .textarea
            on the wrapper container). Tokens render in accent blue so
            they match the chips below. aria-hidden because the textarea
            is the real input. */}
        <div
          ref={backdropRef}
          aria-hidden="true"
          className="textarea"
          style={{
            ...sharedTextStyle,
            position: 'absolute',
            inset: 0,
            overflow: 'auto',
            // Backdrop renders the body text in the theme's normal color
            // and the tokens in accent blue (via the per-segment span).
            // The textarea on top is what's transparent — we want to see
            // THROUGH it to this layer.
            color: 'var(--text-primary)',
            background: 'transparent',
            pointerEvents: 'none',
            // Drop the .textarea border on the backdrop — wrapper owns it.
            border: 0,
            borderRadius: 0,
            // Match textarea's box so the text lays out identically.
            // The actual padding / font come from the .textarea class.
          }}
        >
          {segments.length === 0 ? (
            // Render the placeholder-equivalent whitespace so the
            // backdrop occupies the same vertical space as the empty
            // textarea (helps the wrapper preserve `rows` height).
            <span>&nbsp;</span>
          ) : (
            segments.map((seg, i) =>
              seg.type === 'token' ? (
                <span
                  key={i}
                  style={{
                    color: tokenColor,
                    background: tokenBg,
                    borderRadius: 2,
                    fontWeight: 500,
                  }}
                >
                  {seg.text}
                </span>
              ) : (
                <span key={i}>{seg.text}</span>
              ),
            )
          )}
          {/* Trailing space guard — keeps the backdrop scrollHeight in
              step with the textarea when the value ends in a newline. */}
          {value.endsWith('\n') && <span>&nbsp;</span>}
        </div>

        <textarea
          ref={textareaRef}
          className="textarea"
          rows={rows}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            // Sync scroll on the same tick — typing past the visible
            // window otherwise leaves the backdrop a frame behind.
            requestAnimationFrame(syncScroll);
          }}
          onScroll={syncScroll}
          required={required}
          placeholder={placeholder}
          style={{
            ...sharedTextStyle,
            position: 'relative',
            background: 'transparent',
            // Caret stays the theme color; only the typed text is
            // transparent so the backdrop's colored render shows
            // through. Selection bg still works because the textarea
            // owns the selection.
            color: 'transparent',
            caretColor: 'var(--text-primary)',
            // WebkitTextFillColor is what actually transparentizes the
            // glyphs in Safari/Chrome — `color: transparent` alone is
            // hit-or-miss across browsers.
            WebkitTextFillColor: 'transparent',
            // Drop the .textarea border — wrapper owns it now. Keep the
            // box otherwise identical so the caret aligns to the
            // backdrop's glyphs.
            border: 0,
            borderRadius: 0,
            outline: 'none',
            width: '100%',
            resize: 'vertical',
          }}
        />
      </div>

      {showReferences && tokenNames.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            alignItems: 'center',
            fontSize: 11,
          }}
        >
          <span className="t-secondary" style={{ marginRight: 4 }}>
            References:
          </span>
          {tokenNames.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => jumpTo(name)}
              title={`Jump to first occurrence of {{${name}}}`}
              style={{
                ...chipBase,
                // Slight bg bump on the most-recently-clicked chip.
                background:
                  focusedToken === name
                    ? 'var(--surface-accent-strong, rgba(37, 99, 235, 0.22))'
                    : chipBase.background,
              }}
            >
              {`{{${name}}}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
